import React, { type FC, useMemo } from 'react';
import {
	FormattedMessage,
	injectIntl,
	type WithIntlProps,
	type WrappedComponentProps,
} from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield/text-field';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import Lozenge from '@atlaskit/lozenge/lozenge';
import SearchIcon from '@atlaskit/icon/core/search';
import AddIcon from '@atlaskit/icon/core/add';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import StatusWorkflowReturnedIcon from '@atlaskit/icon-lab/core/status-workflow-returned';

import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { ContentStateColor } from '../../ContentStateColor.tsx';
import { CHANGES_REQUESTED_STATUS_ID } from '../../SpaceAdminConfig/StatusAndApprovalsConfigShared.ts';
import type { ContentState } from '../types.ts';

import type { DefaultSectionItem } from './ContentStateMenuV2.tsx';
import { i18n } from './contentStateMenuV2I18n.ts';

const styles = cssMap({
	dialog: {
		minWidth: '320px',
		maxWidth: '360px',
	},
	row: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		justifyContent: 'space-between',
	},
	searchInput: {
		paddingInline: token('space.150'),
		paddingTop: token('space.100'),
	},
	after: {
		paddingInlineEnd: token('space.075'),
	},
	workflowStatusBadge: {
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		color: token('color.text.subtlest'),
		gap: token('space.100'),
		alignItems: 'center',
	},
	workflowStatusLabel: {
		alignItems: 'center',
		gap: token('space.100'),
	},
	badgeWrapperStyles: {
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.025'),
		paddingInline: token('space.050'),
		borderRadius: token('radius.xsmall'),
	},
	iconStyles: {
		marginLeft: token('space.negative.025'),
	},
});

type StatusOptionRowProps = {
	option: ContentState;
	onSelect: (status: ContentState) => void;
	isActive?: boolean;
};

const StatusOptionRow: FC<StatusOptionRowProps> = ({ option, onSelect, isActive }) => {
	const handleClick = () => {
		if (option && option.name) {
			onSelect(option);
		}
	};

	return (
		<ButtonItem
			isSelected={isActive}
			iconBefore={
				<ContentStateColor
					color={option.color}
					restrictionLevel={option.restrictionLevel}
					appearance="contentStateButton"
				/>
			}
			onClick={handleClick}
		>
			{option.name}
		</ButtonItem>
	);
};

type WorkflowStatusOptionRowProps = {
	status: WorkflowStatus;
	onSelect: (status: WorkflowStatus) => void;
};

const WorkflowStatusOptionRow: FC<WorkflowStatusOptionRowProps> = ({ status, onSelect }) => {
	const handleClick = () => onSelect(status);
	const Icon =
		status.id === CHANGES_REQUESTED_STATUS_ID
			? StatusWorkflowReturnedIcon
			: StatusWorkflowInReviewIcon;

	return (
		<ButtonItem
			iconBefore={
				<Box xcss={styles.iconStyles}>
					<Icon label="" size="small" color={token('color.icon.accent.blue')} spacing="none" />
				</Box>
			}
			onClick={handleClick}
			testId={`workflow-status-option-${status.id}`}
		>
			<Inline xcss={styles.row}>
				<Inline xcss={styles.workflowStatusLabel}>
					{status.allowsReviews ? <FormattedMessage {...i18n.startApproval} /> : status.name}
					{status.allowsReviews && (
						<Lozenge appearance="discovery">
							<FormattedMessage {...i18n.approvalBetaTag} />
						</Lozenge>
					)}
				</Inline>
				{status.allowsReviews ? (
					<ChevronRightIcon size="small" label="" />
				) : (
					<Inline xcss={styles.workflowStatusBadge}>
						<Box xcss={styles.badgeWrapperStyles}>
							<Text size="small" color="color.text.subtlest">
								<FormattedMessage {...i18n.startApproval} />
							</Text>
						</Box>

						<ChevronRightIcon size="small" label="" />
					</Inline>
				)}
			</Inline>
		</ButtonItem>
	);
};

type ContentStateMenuV2SelectViewProps = WrappedComponentProps & {
	searchValue: string;
	onSearchChange: (value: string) => void;
	filteredDefaultSection: DefaultSectionItem[];
	filteredCustomOptions: ContentState[];
	hasResults: boolean;
	currentContentState: ContentState | null;
	onSelectStatus: (status: ContentState) => void;
	onSelectWorkflowStatus: (status: WorkflowStatus) => void;
	onAddCustomClick: () => void;
};

const ContentStateMenuV2SelectViewComponent: FC<ContentStateMenuV2SelectViewProps> = ({
	searchValue,
	onSearchChange,
	filteredDefaultSection,
	filteredCustomOptions,
	hasResults,
	currentContentState,
	onSelectStatus,
	onSelectWorkflowStatus,
	onAddCustomClick,
	intl: { formatMessage },
}) => {
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onSearchChange(e.currentTarget.value);

	const { nonApprovalDefaultRows, approvalRow } = useMemo(() => {
		const nonApproval: DefaultSectionItem[] = [];
		let approval: DefaultSectionItem | undefined;
		for (const item of filteredDefaultSection) {
			if (
				item.type === 'workflowStatus' &&
				item.workflowStatus.allowsReviews &&
				approval === undefined
			) {
				approval = item;
			} else {
				nonApproval.push(item);
			}
		}
		return { nonApprovalDefaultRows: nonApproval, approvalRow: approval };
	}, [filteredDefaultSection]);

	// Section titles ("Default workflow" / "Custom statuses") only render
	// when the user actually has custom statuses to disambiguate from the
	// default ones.
	const defaultSectionTitle =
		filteredCustomOptions.length > 0 ? formatMessage(i18n.defaultWorkflow) : undefined;

	return (
		<Stack xcss={styles.dialog} testId="status-selector-popup-menu">
			<MenuGroup maxHeight={360}>
				<Section>
					<Inline xcss={styles.searchInput}>
						<Textfield
							name="status-search"
							aria-label={formatMessage(i18n.searchPlaceholder)}
							placeholder={formatMessage(i18n.searchPlaceholder)}
							maxLength={20}
							isCompact
							value={searchValue}
							onChange={handleSearchChange}
							elemAfterInput={
								<Box xcss={styles.after}>
									<SearchIcon label="" />
								</Box>
							}
							testId="status-search-input"
						/>
					</Inline>
				</Section>
				<Section isScrollable>
					{nonApprovalDefaultRows.length > 0 && (
						<Section title={defaultSectionTitle}>
							<MenuGroup>
								{nonApprovalDefaultRows.map((item) =>
									item.type === 'contentState' ? (
										<StatusOptionRow
											key={item.contentState.id}
											option={item.contentState}
											onSelect={onSelectStatus}
											isActive={currentContentState?.id === item.contentState.id}
										/>
									) : (
										<WorkflowStatusOptionRow
											key={`workflow-${item.workflowStatus.id}`}
											status={item.workflowStatus}
											onSelect={onSelectWorkflowStatus}
										/>
									),
								)}
							</MenuGroup>
						</Section>
					)}
					{filteredCustomOptions.length > 0 && (
						<Section title={formatMessage(i18n.customStatuses)}>
							{filteredCustomOptions.map((option) => (
								<StatusOptionRow
									key={option.id}
									option={option}
									onSelect={onSelectStatus}
									isActive={currentContentState?.id === option.id}
								/>
							))}
						</Section>
					)}
					{approvalRow && approvalRow.type === 'workflowStatus' && (
						<Section
							hasSeparator={nonApprovalDefaultRows.length > 0 || filteredCustomOptions.length > 0}
						>
							<WorkflowStatusOptionRow
								key={`workflow-${approvalRow.workflowStatus.id}`}
								status={approvalRow.workflowStatus}
								onSelect={onSelectWorkflowStatus}
							/>
						</Section>
					)}
					{!hasResults && searchValue.trim() && (
						<Section>
							<ButtonItem
								iconBefore={<AddIcon label="" />}
								description={formatMessage(i18n.addCustomStatusDescription)}
								onClick={onAddCustomClick}
								testId="add-custom-status-button"
							>
								{formatMessage(i18n.addCustomStatus)}
							</ButtonItem>
						</Section>
					)}
				</Section>
			</MenuGroup>
		</Stack>
	);
};

export const ContentStateMenuV2SelectView: React.FC<
	WithIntlProps<ContentStateMenuV2SelectViewProps>
> & {
	WrappedComponent: React.ComponentType<ContentStateMenuV2SelectViewProps>;
} = injectIntl(ContentStateMenuV2SelectViewComponent);
